import Loading from "../../../components/utils/Loading.js";
import { formattedDate } from "../../../components/formatted/FormattedDateAndTime.js";
import { useTranslation } from "react-i18next";
import InfoTip from "../../../components/InfoTip.js";
import { ReactComponent as HelpIcon } from "../../../static/assets/dashboard-help.svg";
import excavatorIcon from "../../../static/assets/excavator.svg";
import totalDissolvedSolidsIcon from "../../../static/assets/total_dissolved_solids.svg";
import opacityIcon from "../../../static/assets/opacity.svg";
import tunnerIcon from "../../../static/assets/tunner.svg";
import trendingUpIcon from "../../../static/assets/moving.svg";
import trendingDownIcon from "../../../static/assets/trending_down.svg";
import {
  SUB_OPTIMAL_FILLING_THRESHOLD,
  FILLING_RATE_OBJECTIVE,
  computeAverageFillingRate,
  countSubOptimalRotations,
  isPreviousPeriodComparable,
} from "../utils/collectionInsights.js";

const computePercentageChange = (current, previous) => {
  if (!previous || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
};

const TrendChip = ({
  value,
  unit = "%",
  positiveIsGood = true,
  reference,
  previousValue,
}) => {
  const hasValue = Number.isFinite(value);
  const isPositive = hasValue && value > 0;
  const isGood = positiveIsGood ? isPositive : hasValue && value < 0;

  const toneClass =
    !hasValue || value === 0
      ? "bg-gray-100 text-gray-500"
      : isGood
        ? "bg-[#E9FBFB] text-[#008685]"
        : "bg-[#FDECEF] text-[#E5193C]";

  const trendIcon = isGood ? trendingUpIcon : trendingDownIcon;

  return (
    <div
      className={`self-start flex flex-row items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${toneClass}`}
    >
      {hasValue && value !== 0 ? (
        <img src={trendIcon} alt="" width={14} height={8} />
      ) : (
        <i className="fas fa-arrow-right" />
      )}
      <span className="font-semibold">
        {isPositive ? "+" : ""}
        {hasValue ? value : "–"}
        {unit}
      </span>
      {reference ? (
        <span>
          {reference}
          {previousValue != null ? ` (${previousValue})` : ""}
        </span>
      ) : null}
    </div>
  );
};

const KeyValueCard = ({ icon, title, value, infoTip, trend }) => (
  <div className="statistics-card flex flex-col flex-1 min-w-[240px] gap-3">
    <div className="flex flex-row items-center gap-3">
      <img src={icon} alt="" width={52} height={52} />
      <div className="flex flex-col">
        <div className="flex items-center gap-1 text-gray-600">
          <span>{title}</span>
          {infoTip ? <InfoTip icon={HelpIcon}>{infoTip}</InfoTip> : null}
        </div>
        <h3 className="font-bold text-xl text-[#000000]">{value}</h3>
      </div>
    </div>
    {trend}
  </div>
);

const StatisticsKeyValuesComponentCollection = ({
  periodHistoryStart,
  periodHistoryStop,
  periodHistoryStartPrevious,
  periodHistoryStopPrevious,
  enablePreviousPeriod,
  statisticsCollectionCurrent,
  statisticsCollectionPrevious,
  collectionWasteGenerated,
  isLoading,
}) => {
  const { t } = useTranslation();

  const showPreviousTrends = isPreviousPeriodComparable({
    enablePreviousPeriod,
    periodStart: periodHistoryStart,
    periodStop: periodHistoryStop,
    previousPeriodStart: periodHistoryStartPrevious,
    previousPeriodStop: periodHistoryStopPrevious,
    previousStatistics: statisticsCollectionPrevious,
  });

  const currentCount = statisticsCollectionCurrent?.countCollections ?? 0;
  const previousCount = statisticsCollectionPrevious?.countCollections ?? 0;
  const totalWasteVolume =
    collectionWasteGenerated?.totalVolumeMaterial ??
    statisticsCollectionCurrent?.totalVolumeMaterial ??
    0;
  const previousWasteVolume =
    statisticsCollectionPrevious?.totalVolumeMaterial ?? 0;

  const averageFillingRateCurrent = computeAverageFillingRate(
    statisticsCollectionCurrent?.dataResult,
  );
  const averageFillingRatePrevious = computeAverageFillingRate(
    statisticsCollectionPrevious?.dataResult,
  );

  const subOptimalCurrent = countSubOptimalRotations(
    statisticsCollectionCurrent?.collectionsData,
  );
  const subOptimalPrevious = countSubOptimalRotations(
    statisticsCollectionPrevious?.collectionsData,
  );

  const rotationsTrend = showPreviousTrends
    ? computePercentageChange(currentCount, previousCount)
    : null;
  const volumeTrend = showPreviousTrends
    ? computePercentageChange(totalWasteVolume, previousWasteVolume)
    : null;
  const subOptimalTrend = showPreviousTrends
    ? computePercentageChange(subOptimalCurrent, subOptimalPrevious)
    : null;
  const fillingRateTrend = showPreviousTrends
    ? Number(
        (averageFillingRateCurrent - averageFillingRatePrevious).toFixed(1),
      )
    : null;

  const previousPeriodReference = t("vs previous period");
  const objectiveReference = `${t("Objective")} ≥ ${FILLING_RATE_OBJECTIVE}%`;

  return (
    <div>
      {!isLoading ? (
        <section>
          <article className="flex flex-row flex-wrap w-full gap-6">
            <KeyValueCard
              icon={excavatorIcon}
              title={t("Rotations completed")}
              value={currentCount}
              infoTip={t("date.range", {
                from: formattedDate(periodHistoryStart, t),
                to: formattedDate(periodHistoryStop, t),
              })}
              trend={
                showPreviousTrends && rotationsTrend != null ? (
                  <TrendChip
                    value={rotationsTrend}
                    positiveIsGood={true}
                    reference={previousPeriodReference}
                    previousValue={previousCount > 0 ? previousCount : null}
                  />
                ) : null
              }
            />

            <KeyValueCard
              icon={totalDissolvedSolidsIcon}
              title={t("Analyzed waste")}
              value={
                <>
                  {Math.round(totalWasteVolume)} m<sup>3</sup>
                </>
              }
              trend={
                showPreviousTrends && volumeTrend != null ? (
                  <TrendChip
                    value={volumeTrend}
                    positiveIsGood={true}
                    reference={previousPeriodReference}
                  />
                ) : null
              }
            />

            <KeyValueCard
              icon={opacityIcon}
              title={t("Average filling rate")}
              value={`${averageFillingRateCurrent.toFixed(1)}%`}
              infoTip={objectiveReference}
              trend={
                showPreviousTrends && fillingRateTrend != null ? (
                  <TrendChip
                    value={fillingRateTrend}
                    unit="%"
                    positiveIsGood={true}
                    reference={objectiveReference}
                  />
                ) : null
              }
            />

            <KeyValueCard
              icon={tunnerIcon}
              title={t("Sub-optimal rotations")}
              value={subOptimalCurrent}
              infoTip={t("Rotations triggered below the filling threshold", {
                threshold: SUB_OPTIMAL_FILLING_THRESHOLD,
              })}
              trend={
                showPreviousTrends && subOptimalTrend != null ? (
                  <TrendChip
                    value={subOptimalTrend}
                    positiveIsGood={false}
                    reference={previousPeriodReference}
                  />
                ) : null
              }
            />
          </article>
        </section>
      ) : (
        <div className="">
          <Loading height={"35vh"} />
        </div>
      )}
    </div>
  );
};

export default StatisticsKeyValuesComponentCollection;
